import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Loader2, MapPin, Briefcase, Languages, Sparkles, Check, X, Video,
  AlertCircle, ShieldCheck, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Public client portal — view 2-3 anonymized talents and decide.
 *
 * Path: /client/talents/{token}
 * NO AUTH. Token is the only credential.
 */
const ClientPresentationPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyTalentId, setBusyTalentId] = useState(null);
  const [interviewModal, setInterviewModal] = useState(null); // {talent_id, note}

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/client-portal/presentations/${token}`);
        if (!res.ok) {
          if (res.status === 404) throw new Error('Lien invalide ou supprimé');
          if (res.status === 410) throw new Error('Lien expiré ou révoqué');
          throw new Error(`HTTP ${res.status}`);
        }
        const body = await res.json();
        setData(body);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, apiUrl]);

  const decide = async (talentId, decision, note = null) => {
    setBusyTalentId(talentId);
    try {
      const res = await fetch(`${apiUrl}/api/client-portal/presentations/${token}/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ talent_id: talentId, decision, note }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const labelMap = {
        keep: 'Talent gardé',
        reject: 'Talent rejeté',
        request_interview: 'Entretien demandé',
      };
      toast.success(labelMap[decision]);
      // Refresh local state
      setData((prev) => ({
        ...prev,
        talents: prev.talents.map((t) => (t.talent_id === talentId ? { ...t, decision } : t)),
      }));
      setInterviewModal(null);
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setBusyTalentId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-tp-bg to-white">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-tp-primary mx-auto" />
          <p className="text-sm text-tp-text-light mt-3">Chargement de la présentation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white p-6">
        <Card className="max-w-md p-6 text-center border-red-200">
          <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-red-900 mb-2">Présentation indisponible</h1>
          <p className="text-sm text-red-700">{error}</p>
          <p className="text-xs text-red-600/70 mt-3">
            Contactez votre interlocuteur Talent Partner pour un nouveau lien.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-tp-bg/40 to-white pb-12" data-testid="client-portal-presentation">
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
        {/* Header */}
        <div className="mb-6 md:mb-10">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-4 w-4 text-tp-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-tp-primary">
              Talent Partner · présentation confidentielle
            </span>
          </div>
          <h1 className="text-2xl md:text-4xl font-display font-semibold text-tp-text leading-tight">
            {data.client_company ? <>Pour <span className="text-tp-primary">{data.client_company}</span></> : 'Sélection de talents'}
          </h1>
          {data.client_contact && (
            <p className="text-sm text-tp-text-light mt-1">À l'attention de {data.client_contact}</p>
          )}
          {data.notes_for_client && (
            <p className="text-sm text-tp-text mt-3 max-w-2xl whitespace-pre-wrap leading-relaxed">
              {data.notes_for_client}
            </p>
          )}
          {data.expires_at && (
            <div className="flex items-center gap-1.5 mt-3 text-xs text-tp-text-light">
              <Clock className="h-3 w-3" />
              <span>Lien valable jusqu'au {new Date(data.expires_at).toLocaleDateString('fr-FR')}</span>
            </div>
          )}
        </div>

        {/* Talents grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {data.talents.map((t) => (
            <TalentCard
              key={t.talent_id}
              talent={t}
              busy={busyTalentId === t.talent_id}
              onKeep={() => decide(t.talent_id, 'keep')}
              onReject={() => decide(t.talent_id, 'reject')}
              onRequestInterview={() => setInterviewModal({ talent_id: t.talent_id, note: '' })}
            />
          ))}
        </div>

        <p className="text-[11px] text-tp-text-light text-center mt-8">
          Vos décisions sont enregistrées en temps réel et notifient automatiquement votre interlocuteur Talent Partner.
        </p>
      </div>

      {/* Interview request modal */}
      {interviewModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setInterviewModal(null)}>
          <Card
            className="max-w-md w-full p-5 md:p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="request-interview-modal"
          >
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Video className="h-5 w-5 text-blue-600" />
              Demander un entretien vidéo
            </h3>
            <p className="text-sm text-tp-text-light mb-4">
              Votre demande sera transmise à votre interlocuteur Talent Partner qui organisera le call.
            </p>
            <Textarea
              placeholder="Précisions (créneaux préférés, points à creuser, etc.)"
              value={interviewModal.note}
              onChange={(e) => setInterviewModal({ ...interviewModal, note: e.target.value })}
              rows={3}
              className="mb-4"
              data-testid="interview-note-input"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setInterviewModal(null)} data-testid="interview-cancel-btn">
                Annuler
              </Button>
              <Button
                onClick={() => decide(interviewModal.talent_id, 'request_interview', interviewModal.note || null)}
                disabled={busyTalentId === interviewModal.talent_id}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="interview-submit-btn"
              >
                {busyTalentId === interviewModal.talent_id ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Video className="h-4 w-4 mr-2" />
                )}
                Demander l'entretien
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};


const TalentCard = ({ talent, busy, onKeep, onReject, onRequestInterview }) => {
  const decisionStyles = {
    keep: { border: 'border-emerald-300', bg: 'bg-emerald-50/40', label: '✓ Gardé', text: 'text-emerald-700' },
    reject: { border: 'border-red-300', bg: 'bg-red-50/40', label: '✕ Rejeté', text: 'text-red-700' },
    request_interview: { border: 'border-blue-300', bg: 'bg-blue-50/40', label: '🎥 Entretien demandé', text: 'text-blue-700' },
  };
  const decided = !!talent.decision;
  const dStyle = decided ? decisionStyles[talent.decision] : null;

  return (
    <Card
      className={`p-5 border-2 transition ${dStyle?.border || 'border-tp-border'} ${dStyle?.bg || 'bg-white'}`}
      data-testid={`talent-card-${talent.talent_id}`}
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-display font-semibold text-tp-text">{talent.public_name}</h3>
            {talent.score && (
              <Badge className="bg-tp-primary/10 text-tp-primary border-tp-primary/30">
                <Sparkles className="h-3 w-3 mr-1" />
                {talent.score}/100
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-tp-text-light">
            {talent.main_role && <span className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3" />{talent.main_role}</span>}
            {talent.country && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{talent.country}</span>}
            {talent.years_experience && <span>{talent.years_experience}+ ans</span>}
          </div>
        </div>

        {dStyle && (
          <Badge className={`${dStyle.text} bg-white border ${dStyle.border} whitespace-nowrap`}>
            {dStyle.label}
          </Badge>
        )}
      </div>

      {/* AI summary */}
      {talent.ai_summary && (
        <div className="rounded-md bg-tp-primary/5 border border-tp-primary/20 p-3 mb-3">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold text-tp-primary mb-1">
            <Sparkles className="h-3 w-3" />
            Résumé IA
          </div>
          <p className="text-sm text-tp-text leading-snug">{talent.ai_summary}</p>
        </div>
      )}

      {/* Skills */}
      {Array.isArray(talent.skills) && talent.skills.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] uppercase tracking-wide font-semibold text-tp-text-light mb-1.5">Compétences</p>
          <div className="flex flex-wrap gap-1.5">
            {talent.skills.map((s, i) => (
              <span key={`sk-${i}`} className="text-xs bg-tp-bg/60 border border-tp-border/40 px-2 py-0.5 rounded">
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Languages + availability + price */}
      <div className="grid grid-cols-2 gap-2 text-xs mb-4">
        {Array.isArray(talent.languages) && talent.languages.length > 0 && (
          <div>
            <p className="text-tp-text-light flex items-center gap-1"><Languages className="h-3 w-3" />Langues</p>
            <p className="text-tp-text font-medium">{talent.languages.join(', ')}</p>
          </div>
        )}
        {talent.availability && (
          <div>
            <p className="text-tp-text-light">Disponibilité</p>
            <p className="text-tp-text font-medium">{talent.availability}</p>
          </div>
        )}
        {talent.salary_estimate_eur != null && (
          <div className="col-span-2 mt-1 rounded-md bg-amber-50 border border-amber-200 p-2">
            <p className="text-[11px] text-amber-800/80 uppercase tracking-wide">Tarif client estimatif</p>
            <p className="text-base font-bold text-amber-900">{Number(talent.salary_estimate_eur).toLocaleString('fr-FR')} € / mois</p>
          </div>
        )}
      </div>

      {/* Video intro */}
      {talent.video_intro_url && (
        <a
          href={talent.video_intro_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-tp-primary hover:underline mb-3"
          data-testid={`talent-video-${talent.talent_id}`}
        >
          <Video className="h-3 w-3" />
          Voir la vidéo d'introduction
        </a>
      )}

      {/* Actions */}
      {!decided ? (
        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-tp-border/50">
          <Button
            size="sm"
            onClick={onKeep}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`talent-keep-${talent.talent_id}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Garder</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onRequestInterview}
            disabled={busy}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid={`talent-interview-${talent.talent_id}`}
          >
            <Video className="h-4 w-4 mr-1" />
            Entretien
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReject}
            disabled={busy}
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid={`talent-reject-${talent.talent_id}`}
          >
            <X className="h-4 w-4 mr-1" />
            Rejeter
          </Button>
        </div>
      ) : (
        <p className="text-[11px] text-tp-text-light pt-3 border-t border-tp-border/50 text-center italic">
          Décision enregistrée. Votre interlocuteur Talent Partner a été notifié.
        </p>
      )}
    </Card>
  );
};

export default ClientPresentationPage;
